import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import Home from './pages/Home.vue';
import Rooms from './pages/rooms/Rooms.vue';
import Room from './pages/rooms/Room.vue';
import { setLanguage, getUrlLanguage } from './composables/useLanguage';
import { Languages } from './composables/useTranslations';

const baseRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: Home,
  },
  {
    path: '/rooms',
    component: Rooms,
  },
  {
    path: '/rooms/:roomId',
    component: Room,
  },
];

// 🔄 Convert `/path` → `path` for nested routes
function toRelativePath(route: RouteRecordRaw): RouteRecordRaw {
  return {
    ...route,
    path: route.path === '/' ? '' : route.path.replace(/^\//, ''),
  };
}

// 🌍 Create localized versions of routes
function createLocalizedRoutes(
  locales: Languages[],
  base: RouteRecordRaw[],
): RouteRecordRaw[] {
  return locales.map((locale) => ({
    path: `/${locale}`,
    children: base.map(toRelativePath),
  }));
}

const localizedRoutes = createLocalizedRoutes(['de', 'es'], baseRoutes);

// ✅ Redirect `/en/*` → `/*`
const redirectEn: RouteRecordRaw = {
  path: '/en/:pathMatch(.*)*',
  redirect: (to) => '/' + (to.params['pathMatch'] || ''),
};

const routes: RouteRecordRaw[] = [
  ...baseRoutes,
  ...localizedRoutes,
  redirectEn,
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 🔤 Handle language state from URL
router.beforeEach((to, _from, next) => {
  // Strip trailing slashes (except for root "/")
  if (to.path !== '/' && to.path.endsWith('/')) {
    return next(
      to.path.replace(/\/+$/, '') + to.fullPath.slice(to.path.length),
    );
  }

  // Set the language from URL
  const langFromUrl = getUrlLanguage(to.path);
  setLanguage(langFromUrl);
  next();
});
